import React from "react";

import { Card } from "@/components/flowbite/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/flowbite/tooltip";
import Typography from "@/components/flowbite/typography/typography";
import { PolygonPreview } from "@/components/maps/polygon-preview";
import { DEFAULT_MAP_STYLE } from "@/providers/maps/constants";
import { getMapVisualThemeByStyle } from "@/providers/maps/map-visual-theme.config";
import { getFirstRingFromGeometry } from "@/providers/maps/utils/get-first-ring-from-geometry";
import type { ISchoolBoundaryResponse } from "@/providers/store/api/schools/schools.interface";
import { resolveBoundarySchoolsDisplayName } from "@/shared/utils/resolve-boundary-schools-display-name";

import { SectionTitle } from "./section-title";

interface SectionAttendanceZoneBoundaryProps {
  schoolBoundaries: ISchoolBoundaryResponse[] | null;
  schoolNameFallback: string;
  color?: string;
}

const defaultMapVisualTheme = getMapVisualThemeByStyle(DEFAULT_MAP_STYLE);

export const SectionAttendanceZoneBoundary: React.FC<
  SectionAttendanceZoneBoundaryProps
> = ({ schoolBoundaries, schoolNameFallback, color }) => {
  return (
    <div className="flex flex-col gap-2">
      <SectionTitle title="Attendance Zone Boundary" />

      <div className="flex w-full flex-col gap-2">
        {schoolBoundaries?.map((schoolBoundary) => {
          const firstRing = schoolBoundary
            ? getFirstRingFromGeometry(schoolBoundary.geometry)
            : null;
          const displayName = resolveBoundarySchoolsDisplayName(schoolBoundary?.schools, {
            unassignedLabel: schoolNameFallback,
          });

          return (
            <Card
              key={schoolBoundary.id}
              className="flex min-w-0 items-center gap-2 p-2.5 pl-3"
            >
              {schoolBoundary ? (
                <>
                  {firstRing ? (
                    <PolygonPreview
                      coordinates={firstRing}
                      fillColor={color}
                      fillOpacity={color ? defaultMapVisualTheme.polygonFillOpacity : undefined}
                      size={40}
                      strokeColor={color}
                      strokeOpacity={color ? defaultMapVisualTheme.polygonLineOpacity : undefined}
                    />
                  ) : null}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="min-w-0 flex-1">
                        <Typography.BodyMRegular
                          className="truncate"
                          colorShade={400}
                        >
                          {displayName}
                        </Typography.BodyMRegular>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent
                      className="max-w-[360px] whitespace-normal"
                      showArrow={false}
                    >
                      {displayName}
                    </TooltipContent>
                  </Tooltip>
                </>
              ) : (
                <Typography.BodyMRegular colorShade={400}>
                  None
                </Typography.BodyMRegular>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};
